import { Link } from 'react-router'
import { Button } from '@/components/ui/button'

/**
 * Consent prompt.
 *
 * The captured site loaded GA4, Hotjar, the Meta Pixel, HubSpot and RB2B on
 * every page with no prompt at all. `ask` (Europe) holds every tag until a
 * choice is made; `notice` (everywhere else) runs them by default and offers
 * a way to decline.
 */
export function ConsentBanner({
  onDecide,
  mode = 'ask',
}: {
  onDecide: (choice: { analytics: boolean; marketing: boolean }) => void
  mode?: 'ask' | 'notice'
}) {
  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-[var(--z-above-legacy)] bg-neutral-900 px-6 py-5"
    >
      <div className="mx-auto flex max-w-main flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
        {/*
          `leading-[1.4]` matches `.small-text`'s own 1.4em ratio elsewhere on
          the site — set as a unitless multiplier so it scales off this
          element's own 14px, not an ancestor's. Left unset, this inherited
          `body`'s `line-height: 1.62em`, which is an *absolute* 31px computed
          against body's larger 19.2px type size — 2.2× this text's own size,
          not the 1.4× every other small label on the site uses.
        */}
        <p className="m-0 max-w-[62ch] text-small leading-[1.4] text-dark-blue-50">
          We use cookies to understand how the site is used and to measure our marketing.{' '}
          {mode === 'notice'
            ? 'You can decline and we\u2019ll stop.'
            : 'You can decline without affecting anything on the site.'}{' '}
          <Link to="/privacy" className="text-white underline">
            Privacy policy
          </Link>
        </p>
        <div className="flex flex-none flex-wrap gap-3">
          <Button
            variant="outlineWhite"
            size="small"
            onClick={() => onDecide({ analytics: false, marketing: false })}
          >
            Decline
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => onDecide({ analytics: true, marketing: true })}
          >
            {mode === 'notice' ? 'OK' : 'Accept'}
          </Button>
        </div>
      </div>
    </div>
  )
}
